import { useNoosSupplyPlanDetailContext } from "~/context/NoosSupplyPlanDetailProvider"
import { parseSettings } from "~/hooks/useSupplyPlan"
import { api } from "~/utils/api"
import { buildNoosSuggestedOrdersInput } from "~/utils/closingStockUtils"
import { getBuyCoverageWindow } from "~/utils/noosSuggestionWindow"

/**
 * The plan's suggestion for a buy placed on `buyDate`, scored over the window that buy covers.
 * Triggers, Create buy and Add to buy all read it, so a previewed quantity is what gets written.
 */
export function useNoosBuySuggestion({
  variantIds,
  buyDate,
  isScoped,
  warehouseId,
  excludePurchasePlanId,
  enabled = true,
}: {
  variantIds: string[]
  buyDate: Date
  isScoped: boolean
  warehouseId?: string
  // A buy's own draft POs mustn't count as intent against itself.
  excludePurchasePlanId?: number
  enabled?: boolean
}) {
  const { supplyPlan, nextBuyDate } = useNoosSupplyPlanDetailContext()
  const settings = parseSettings(supplyPlan.settings)
  const { coverUntil, coverageDays } = getBuyCoverageWindow({
    buyDate,
    nextBuyDate,
    purchaseInterval: settings.purchaseInterval,
    isScoped,
  })
  const input = buildNoosSuggestedOrdersInput({
    variantIds,
    // Without it the backend anchors arrival at `max(earliest safety-stock-out, today + leadTime)`.
    orderDate: buyDate,
    nextOrderDate: coverUntil,
    purchaseIntervalDays: coverageDays,
    warehouseId,
    respectMoq: settings.respectMoq,
    minMoqThresholdPct: settings.minMoqThresholdPct,
    noSafetyStockWarehouseIds: settings.noSafetyStockWarehouseIds,
    includeDraftOrdersAsIntent: settings.countDraftOrdersAsIntent,
    excludePurchasePlanId,
  })
  const query = api.stockOutTimes.getSuggestedOrders.useQuery(input, {
    enabled: enabled && variantIds.length > 0,
  })
  return { coverUntil, input, query }
}
